/**
 * Warstwa API modułu: H — cele pracowników.
 * Właściciel: FALA 1, agent H-1 (H-3/H-4 dopisują swoje) (docs/PROJEKT-00-FUNDAMENT-2026-09-02.md §5, §6).
 * Trasy backendu: api/goals.
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod, które apiService.js doczepia do wspólnego `apiService` (jedno miejsce,
 * src/api/modules/index.js). Nazwy metod muszą być unikalne w całej aplikacji — kolizja zatrzymuje start
 * (patrz attachApiModules). Tego pliku nie dotyka nikt poza właścicielem modułu.
 *
 * Zapadka `GoalsEndpointyMajaWpiecieWeFroncieTests` (backend) porównuje ten plik z akcjami
 * `GoalsController`: endpoint bez metody TUTAJ = czerwony test. Powód: endpoint, do którego nie prowadzi
 * żaden przycisk, jest martwym kodem, a metoda bez endpointu to 404 pod przyciskiem.
 */
export function attach(api) {
    return {
        // ── Pracownik ────────────────────────────────────────────────────
        getMyGoals: (params) => api.get("/goals/my", { params }),
        getGoalParents: (params) => api.get("/goals/parents", { params }),
        getGoal: (id) => api.get(`/goals/${id}`),
        createGoal: (data) => api.post("/goals", data),
        updateGoal: (id, data) => api.put(`/goals/${id}`, data),
        deleteGoal: (id) => api.delete(`/goals/${id}`),
        // [R3, 2026-09-16] `data` NIESIE UZASADNIENIE i jest obowiazkowe po stronie serwera:
        // { currentValue, note }. Serwer odmawia przy pustej notatce i przy zbyt duzym skoku.
        updateGoalProgress: (id, data) => api.post(`/goals/${id}/progress`, data),
        // [R3] Odhaczenie kamienia tez ma cialo zadania — bez niego nie bylo gdzie wpisac powodu.
        setGoalMilestone: (id, milestoneId, done, data) =>
            api.post(`/goals/${id}/milestones/${milestoneId}`, data || {}, { params: { done } }),
        completeGoal: (id) => api.post(`/goals/${id}/complete`),
        uncompleteGoal: (id) => api.post(`/goals/${id}/uncomplete`),
        resubmitGoal: (id) => api.post(`/goals/${id}/resubmit`),
        // [zgloszenie wlasciciela 2026-09-08] Wlasciciel przesyla swoja propozycje do kadr. Status sie
        // nie zmienia - zmienia sie to, KTO o niej wie. Dla osoby bez przelozonego to jedyna droga naprzod.
        escalateGoalToHr: (id) => api.post(`/goals/${id}/to-hr`),

        // ── Przełożony ───────────────────────────────────────────────────
        getTeamGoals: (params) => api.get("/goals/team", { params }),
        getTeamGoalsSummary: (params) => api.get("/goals/team/summary", { params }),
        getGoalAssignableUsers: (params) => api.get("/goals/team/assignable", { params }),
        createGoalsBatch: (data) => api.post("/goals/batch", data),
        acceptGoal: (id, data) => api.post(`/goals/${id}/accept`, data || {}),
        rejectGoal: (id, data) => api.post(`/goals/${id}/reject`, data || {}),
        cancelGoal: (id, data) => api.post(`/goals/${id}/cancel`, data || {}),
        // [R1, 2026-09-16] Przelozony odsyla zgloszony cel do poprawy — z OBOWIAZKOWYM powodem.
        // Druga, obok oceny, droga odblokowania celu zamrozonego dla wlasciciela.
        sendGoalBack: (id, data) => api.post(`/goals/${id}/send-back`, data || {}),
        evaluateGoal: (id, data) => api.post(`/goals/${id}/evaluate`, data),
        // [N-4, 2026-09-08] Suwak oceniającego (zmniejszenie: raz, maks. o połowę) + publikacja ukrytej oceny.
        setGoalEvaluatorValue: (id, data) => api.post(`/goals/${id}/evaluator-value`, data),
        publishGoalEvaluation: (id) => api.post(`/goals/${id}/publish-evaluation`),

        // ── Powiązania z aplikacją (etap 4) ──────────────────────────────
        getGoalLinkCandidates: (id, params) => api.get(`/goals/${id}/link-candidates`, { params }),
        addGoalLink: (id, data) => api.post(`/goals/${id}/links`, data),
        removeGoalLink: (id, linkId) => api.delete(`/goals/${id}/links/${linkId}`),
        setGoalProgressSource: (id, data) => api.post(`/goals/${id}/progress-source`, data),

        // ── Kadry (etap 3) ───────────────────────────────────────────────
        getHrGoals: (params) => api.get("/goals/hr", { params }),
        getHrGoalsSummary: (params) => api.get("/goals/hr/summary", { params }),
        getHrGoalAssignableUsers: (params) => api.get("/goals/hr/assignable", { params }),
        createHrGoalsBatch: (data) => api.post("/goals/hr/batch", data),
        /**
         * [zgłoszenie właściciela 2026-09-02, poz. 5] Działy, dla których TA osoba może założyć cel działu.
         * Szuflada celu firmy/działu bierze listę stąd, a nie z pełnego słownika działów: serwer przy
         * zapisie i tak przyjmie wyłącznie dział z tego zakresu.
         */
        getGoalPlanScope: () => api.get("/goals/plan-scope"),
        createCompanyGoal: (data) => api.post("/goals/company", data),
        updateCompanyGoal: (id, data) => api.put(`/goals/company/${id}`, data),
        verifyGoalReview: (id, data) => api.post(`/goals/${id}/hr-verify`, data || {}),
        returnGoalReview: (id, data) => api.post(`/goals/${id}/hr-return`, data || {}),

        // ── Karta celów okresu (etap 5) ──────────────────────────────────
        //
        // BAJTY wydanego dokumentu pobiera się z REJESTRU WYDAŃ (`/document-issues/{id}/pdf`),
        // nie z `/goals`: tam stoi bramka po rodzaju źródła, kontrola SHA-256 i dziennik otwarć
        // (fundament §1.1). Ta metoda jest jedynie skrótem — adres jest rejestru, nie modułu celów.
        getGoalReviewCard: (userId, period) =>
            api.get("/goals/review-card", { params: { userId, period: period || undefined } }),
        previewGoalReviewCard: (userId, period) =>
            api.get("/goals/review-card/preview-html", { params: { userId, period: period || undefined } }),
        issueGoalReviewCard: (payload) => api.post("/goals/review-card/issue", payload),
        getGoalReviewCardPdf: (issueId) =>
            api.get(`/document-issues/${issueId}/pdf`, { responseType: "blob" }),

        // ── Historia ─────────────────────────────────────────────────────
        getGoalsHistory: (userId, params) => api.get(`/goals/history/${userId}`, { params }),
    };
}
